'use client';

import React, { useMemo } from 'react';

import type { DrawerHeaderProps } from '@rulebooks/evidend-titan/dist/types/components/Drawer/DrawerHeader';
import type { DrawerHeaderAvatarProps } from '@rulebooks/evidend-titan/dist/types/components/Drawer/DrawerHeaderAvatar';
import { DrawerFooter, DrawerHeader, withTitan } from '@rulebooks/evidend-titan';
import { DrawerHeaderAvatar } from './DrawerHeaderAvatar';

export interface DrawerContainerProps
  extends
    Pick<DrawerHeaderProps, 'closeIcon' | 'hasCloseButton' | 'labelClose' | 'subtitle' | 'variant'>,
    Pick<DrawerHeaderAvatarProps, 'avatar' | 'avatarIcon' | 'initials'> {
  /** The header of the container. */
  header?: React.ReactNode;
  /** The header variant. */
  headerVariant?: 'avatar';
  /** Metadata passed to the drawer header. */
  headerMeta?: DrawerHeaderProps['metadata'];
  /** Avatar header variant: initials. */
  initials?: string;
  /** Optional drawer footer. */
  footer?: React.ReactNode;
  headerRef?: React.Ref<HTMLDivElement>;
  children?: React.ReactNode;
}

const DrawerContainer = withTitan<DrawerContainerProps>(
  (
    {
      headerVariant,
      header,
      headerMeta,
      subtitle,
      footer,
      hasCloseButton,
      labelClose,
      closeIcon,
      children,
      variant = 'primary',
      avatar,
      avatarIcon,
      initials,
      headerRef,
    },
    testId = 'drawerContainer',
  ) => {
    const headerComponent = useMemo(() => {
      switch (headerVariant) {
        case 'avatar':
          return (
            <DrawerHeaderAvatar
              ref={headerRef}
              avatar={avatar}
              avatarIcon={avatarIcon}
              closeIcon={closeIcon}
              hasCloseButton={hasCloseButton}
              initials={initials}
              label={header}
              labelClose={labelClose}
              metadata={headerMeta}
            />
          );
        default:
          return (
            <DrawerHeader
              ref={headerRef}
              closeIcon={closeIcon}
              hasCloseButton={hasCloseButton}
              label={header}
              labelClose={labelClose}
              metadata={headerMeta}
              subtitle={subtitle}
              variant={variant}
            />
          );
      }
    }, [
      headerVariant,
      header,
      headerRef,
      avatar,
      initials,
      avatarIcon,
      headerMeta,
      closeIcon,
      labelClose,
      hasCloseButton,
      subtitle,
      variant,
    ]);

    return (
      <>
        {(header || hasCloseButton) && headerComponent}
        <div data-test-id={testId}>{children}</div>
        {footer && <DrawerFooter variant={variant}>{footer}</DrawerFooter>}
      </>
    );
  },
  { name: 'DrawerContent' },
);

export { DrawerContainer };
